import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/adminService';
import { User, AdminUserCreate, AdminUserUpdate } from '../../types/user';

interface UserFormProps {
  user?: User | null;
  onClose: () => void;
  onSuccess: () => void;
}

const UserForm: React.FC<UserFormProps> = ({ user, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'USER' as 'USER' | 'ADMIN' | 'SUPER_ADMIN' | 'INSURER',
    is_active: true,
    insurer_org_id: undefined as number | undefined,
    org_role: '' as '' | 'org_admin' | 'org_member',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [insurerOrgs, setInsurerOrgs] = useState<Array<{ id: number; name: string }>>([]);

  useEffect(() => {
    const loadInsurerOrgs = async () => {
      try {
        const response = await adminService.listInsurerOrgs('active');
        setInsurerOrgs(response.insurers.map((org) => ({ id: org.id, name: org.name })));
      } catch (err) {
        console.error('Failed to load insurer organizations:', err);
      }
    };

    loadInsurerOrgs();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        full_name: user.full_name || '',
        password: '',
        role: user.role,
        is_active: user.is_active,
        insurer_org_id: user.insurer_org_id,
        org_role: (user.org_role as 'org_admin' | 'org_member' | undefined) || '',
        notes: user.notes || ''
      });
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (user) {
        // Update existing user
        const updateData: AdminUserUpdate = {
          email: formData.email,
          full_name: formData.full_name,
          role: formData.role,
          is_active: formData.is_active,
          insurer_org_id: formData.insurer_org_id || undefined,
          org_role: formData.role === 'INSURER' && formData.org_role
            ? formData.org_role
            : undefined,
          notes: formData.notes
        };

        // Only include password if it's provided
        if (formData.password) {
          updateData.password = formData.password;
        }

        await adminService.updateUser(user.id, updateData);
      } else {
        // Create new user
        const createData: AdminUserCreate = {
          email: formData.email,
          full_name: formData.full_name,
          password: formData.password,
          role: formData.role,
          is_active: formData.is_active,
          insurer_org_id: formData.insurer_org_id || undefined,
          org_role: formData.role === 'INSURER' && formData.org_role
            ? formData.org_role
            : undefined,
          notes: formData.notes
        };

        await adminService.createUser(createData);
      }

      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.detail || `Failed to ${user ? 'update' : 'create'} user`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
      };
      if (name === 'role') {
        if (value !== 'INSURER') {
          next.org_role = '';
        }
        if (value !== 'USER' && value !== 'INSURER') {
          next.insurer_org_id = undefined;
        }
      }
      return next;
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {user ? 'Edit User' : 'Create New User'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password {user ? '(leave blank to keep current)' : '*'}
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required={!user}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role *
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="USER">User</option>
                <option value="ADMIN">Admin</option>
                <option value="SUPER_ADMIN">Super Admin</option>
                <option value="INSURER">Insurer</option>
              </select>
            </div>

            {/* Partner org assignment — partner clients and insurer staff */}
            {(formData.role === 'USER' || formData.role === 'INSURER') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Insurance partner organization
                  {formData.role === 'INSURER' ? ' *' : ''}
                </label>
                <select
                  name="insurer_org_id"
                  value={formData.insurer_org_id || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      insurer_org_id: value === '' ? undefined : parseInt(value, 10)
                    }));
                  }}
                  required={formData.role === 'INSURER'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">
                    {formData.role === 'USER' ? 'None (direct customer)' : 'Select organization'}
                  </option>
                  {insurerOrgs.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {formData.role === 'USER'
                    ? 'Partner clients are scoped to one insurer org for tabletop credits.'
                    : 'Insurer users must belong to an organization.'}
                </p>
              </div>
            )}

            {formData.role === 'INSURER' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization role *
                </label>
                <select
                  name="org_role"
                  value={formData.org_role}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select role</option>
                  <option value="org_admin">Org admin</option>
                  <option value="org_member">Org member</option>
                </select>
              </div>
            )}

            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700">
                Active
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Optional notes about this user..."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : (user ? 'Update User' : 'Create User')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserForm;
